import type {
  InfiniteListPage,
  InfiniteListQueryFn,
  UseInfiniteListProps,
  UseInfiniteListWrapperProps,
} from "./infinite-list.type";
import type { ApiError } from "@/services/api";
import type { InfiniteData, QueryKey } from "@tanstack/react-query";

import { useInfiniteQuery } from "@tanstack/react-query";
import get from "lodash/get";

import { client } from "@/config/client";

import {
  getInfiniteListPageData,
  getInfiniteQueryNextPageParam,
} from "./infinite-list.helper";

export const useInfiniteListWrapper = <Data = unknown>({
  queryKey,
  queryFn,
  ...queryOptions
}: UseInfiniteListWrapperProps<Data>) => {
  const { data: infiniteData, ...queryResult } = useInfiniteQuery<
    InfiniteListPage<Data>,
    ApiError,
    InfiniteData<InfiniteListPage<Data>, number>,
    QueryKey,
    number
  >({
    queryKey,
    queryFn: async (options) => {
      const response = await queryFn(options);
      const payload = response.data;
      return getInfiniteListPageData(payload);
    },
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    getNextPageParam: getInfiniteQueryNextPageParam,
    ...queryOptions,
  });

  const pages = infiniteData?.pages ?? [];
  const lastIndex = pages.length - 1;

  // Make data from all pages directly accessible via `data`
  const data = pages.flatMap((page) => page.data) ?? [];

  // Return API pagination data from the pages fetched
  const pagination = pages[lastIndex]?.paginationParams ?? {};

  return {
    data,
    pages,
    pagination,
    ...queryResult,
  };
};

export const useInfiniteList = <Data = unknown>({
  model,
  params = {},
  enabled = true,
  neverStale = false,
  rootId = null,
}: UseInfiniteListProps) => {
  const queryKey = [model, "list", rootId, params].filter(Boolean);

  const queryFn: InfiniteListQueryFn<Data> = ({ pageParam = 1 }) => {
    const listParams = { ...params, ["page[number]"]: pageParam };
    return rootId
      ? get(client, model).list(rootId, listParams)
      : get(client, model).list(listParams);
  };

  return useInfiniteListWrapper({
    queryKey,
    queryFn,
    enabled,
    staleTime: neverStale ? Infinity : 0,
  });
};
